import * as Icons from 'lucide-react';
import PremiumIconChip from './PremiumIconChip';

const REASONS = [
  {
    icon: 'Zap',
    title: 'Ship in minutes, not months',
    body: 'Blueprint, code, database, deploy \u2014 the whole cycle in one conversation. What used to take an engineering team a quarter now happens over coffee.',
    accent: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.55)',
  },
  {
    icon: 'DollarSign',
    title: 'Save $2,000\u2013$25,000 / month',
    body: 'A DIY tech stack (LLM APIs + infra + tools + team) costs $800\u2013$2,500/mo for a solo dev. We start at $20. And you don\u2019t hire.',
    accent: 'from-emerald-400 to-teal-500',
    glow: 'rgba(16,185,129,0.55)',
  },
  {
    icon: 'Brain',
    title: 'Multi-model intelligence',
    body: 'Claude Sonnet 4.5, GPT-5, and Gemini 2.5 Pro on tap. Auto-routed to the best model for each task \u2014 or pick manually.',
    accent: 'from-violet-500 to-fuchsia-500',
    glow: 'rgba(217,70,239,0.55)',
  },
  {
    icon: 'ShieldCheck',
    title: 'Own your code, forever',
    body: 'One-click GitHub sync. Every project is portable to Vercel, Railway, AWS or your own cluster. Zero vendor lock-in.',
    accent: 'from-sky-400 to-blue-600',
    glow: 'rgba(56,189,248,0.55)',
  },
  {
    icon: 'Globe',
    title: 'Global edge deployment',
    body: 'Ship to 190+ locations with a custom domain, SSL and CDN wired up in 15 minutes. Your users get sub-100ms responses everywhere.',
    accent: 'from-cyan-400 to-teal-500',
    glow: 'rgba(0,240,255,0.55)',
  },
  {
    icon: 'TrendingUp',
    title: 'Built to grow with you',
    body: 'From weekend project to Fortune-500 scale. Same tool. Same conversation. No re-platforming, no re-hiring, no re-learning.',
    accent: 'from-rose-400 to-red-500',
    glow: 'rgba(244,63,94,0.55)',
  },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      className="relative py-24 md:py-28 text-white"
      style={{
        background:
          'radial-gradient(900px 500px at 20% 30%, rgba(255,0,127,0.08) 0%, transparent 60%),' +
          'linear-gradient(180deg, #030303 0%, #000 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FF007F] font-semibold uppercase mb-4 font-mono">
            <Icons.Award size={12} />
            Why teams pick us
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-[-0.03em] text-white">
            The{' '}
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
              unfair advantage
            </span>
            <br />
            for modern founders.
          </h2>
          <p className="mt-5 text-white/60 text-lg leading-relaxed">
            Six reasons founders, agencies and enterprises switch to us
            &mdash; and never go back to DIY.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {REASONS.map((r, i) => {
            const Icon = Icons[r.icon] || Icons.Sparkles;
            return (
              <div
                key={r.title}
                className="reveal group relative glass rounded-2xl p-6 transition duration-300 hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_25px_60px_-25px_rgba(255,0,127,0.35)]"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <PremiumIconChip Icon={Icon} gradient={r.accent} glow={r.glow} size={56} iconSize={24} />
                <div className="mt-4 font-display font-bold text-white text-lg tracking-tight">
                  {r.title}
                </div>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">
                  {r.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
